import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OAuthModule, OAuthModuleConfig, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { DevExtremeModule } from 'devextreme-angular';
import { appInitializer } from 'grow-angular/app-initializer';
import { AcceptLanguageInterceptor, ACCEPT_LANGUAGE_CONFIG_TOKEN } from 'grow-angular/interceptors';
import {
  AuthService,
  AuthServiceConfig,
  AUTH_SERVICE_CONFIG_TOKEN,
  DateTimeConfig,
  DateTimeService,
  DATETIME_SERVICE_CONFIG_TOKEN,
  TranslationService,
  TRANSLATION_SERVICE_CONFIG_TOKEN,
} from 'grow-angular/services';
import { error } from '../debug';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { allowedLocales, apiUrl, appLocale, defaultDateFormat } from './config';
import { GraphQLModule } from './graphql.module';
import { CustomTranslateHttpLoader, PageTitleStrategy } from './shared/services';
import { SharedModule } from './shared/shared.module';
import { SharedFrontendModule } from './shared-frontend/shared-frontend.module';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmDialogComponent } from './pages-frontend/utils/confirm-dialog/confirm-dialog.component';

// KS TEST Router ==> error
/*
import { RouteReuseStrategy, BaseRouteReuseStrategy } from '@angular/router';
export class NoReuseStrategy extends BaseRouteReuseStrategy {
  shouldReuseRoute(): boolean {
    return false;
  }
}
  */

//import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

export function createTranslateLoader(http: HttpClient) {
  return new CustomTranslateHttpLoader(http, './assets/i18n/', `.json?v=${Date.now()}`);
}

export function storageFactory(): OAuthStorage {
  return localStorage;
}

//TODO: refactor config setup(?)
export function oAuthConfigFactory(): OAuthModuleConfig {
  const idmUrl = window.__CONFIG?.IDM_ISSUER || '';
  if (!idmUrl) {
    error('invalid config - IDM_ISSUER not set');
  }
  return {
    resourceServer: {
      allowedUrls: [idmUrl],
      sendAccessToken: true,
    },
  };
}

// KS, 20260915
//
// Entfernt 'offline_access' aus dem angefragten OAuth-Scope -- app-lokaler Workaround.
//
// PROBLEM
// -------
// grow-angular/services/auth.service.ts erzwingt in createAuthConfig() fuer JEDE App
// die Scopes ['openid', 'offline_access', 'profile', 'email']. Durch 'offline_access'
// stellt Keycloak beim Code-Austausch kein normales Refresh Token aus, sondern ein
// OFFLINE Token (erkennbar an 'typ': 'Offline' im JWT und an refresh_expires_in: 0 in
// der Token-Response -- also: laeuft nie ab).
//
// Ein Offline Token haengt an einer eigenen OFFLINE-Session. Jeder Refresh aktualisiert
// deren lastSessionRefresh, fasst die regulaere Online-/SSO-Session aber NICHT an.
// Folge: die SSO-Session laeuft trotz laufender, fehlerfreier Refreshes in
// "SSO Session Idle" und verschwindet. Beobachtet wurde das nur dann nicht, wenn
// zufaellig ein iframe-Bereich offen war -- die dort eingebettete Anwendung macht ihren
// eigenen OIDC-Vorgang gegen Keycloak und hat damit die SSO-Session am Leben gehalten.
//
// Ein Browser-SPA im Authorization-Code-Flow braucht 'offline_access' nie. Der Scope ist
// fuer Daemons und Hintergrunddienste gedacht. Zusaetzlich ist ein Offline Token im
// localStorage ein unbefristetes Credential und damit auch sicherheitstechnisch unschoen.
//
// WO DAS EIGENTLICH HINGEHOERT
// ----------------------------
// Die Korrektur gehoert in grow-angular/services/auth.service.ts, createAuthConfig():
//
//     const requiredScopes = ['openid', 'offline_access', 'profile', 'email'];
//  -> const requiredScopes = ['openid', 'profile', 'email'];
//
// Dort ist die Ursache, und dort haetten auch alle anderen Apps das Problem behoben --
// denn solange das so bleibt, halten die ebenfalls Offline-Sessions statt der SSO-Session
// am Leben und legen unbefristete Tokens im localStorage ab.
//
// WARUM TROTZDEM HIER
// -------------------
// grow-angular wird von mehreren Apps geteilt, die wir aktuell nicht kontrollieren und
// nicht durchtesten koennen. Eine Aenderung an der gemeinsamen Library koennte dort
// Seiteneffekte ausloesen (Apps, die sich bewusst oder unbewusst auf das nie ablaufende
// Token stuetzen). Deshalb bleibt die Library vorerst unangetastet und die Korrektur
// erfolgt app-lokal. Sobald die Aenderung in grow-angular mit den anderen Apps abgestimmt
// ist, kann dieser Wrapper ersatzlos entfallen und wieder direkt appInitializer als
// useFactory eingetragen werden.
//
// WARUM ALS WRAPPER UM appInitializer
// -----------------------------------
// Der Scope muss korrigiert sein, BEVOR runInitialLoginSequence() laeuft, und NACHDEM
// AuthService konstruiert wurde (dessen Konstruktor ruft configure() und setzt damit
// oauthService.scope). Beides ist hier garantiert: AuthService ist Dependency dieser
// Factory, und die Bereinigung laeuft synchron unmittelbar vor dem Login-Vorgang. Ein
// separater, vorgelagerter APP_INITIALIZER wuerde sich dagegen auf die Reihenfolge der
// Provider-Registrierung verlassen.
//
// OAuthService extends AuthConfig, 'scope' ist also eine normale beschreibbare Property.
// Sie ist die einzige Quelle sowohl fuer den Authorization-Request als auch fuer den
// Refresh-Request -- eine Stelle genuegt.
//
// PRUEFUNG NACH DER AENDERUNG (localStorage vorher leeren bzw. neu anmelden, der alte
// Offline Token liegt sonst weiter dort): in der Token-Response muss refresh_expires_in
// dem SSO-Session-Idle-Wert entsprechen (statt 0), und 'typ' im Refresh Token muss
// 'Refresh' sein. Bereits angelegte Offline-Sessions in Keycloak sollten revoked werden.
export function appInitializerWithoutOfflineAccess(
  authService: AuthService,
  translation: TranslationService,
  dateTime: DateTimeService,
  oauthService: OAuthService
) {
  return () => {
    oauthService.scope = (oauthService.scope ?? '')
      .split(/\s+/)
      .filter((scope) => scope !== 'offline_access')
      .join(' ');

    return appInitializer(authService, translation, dateTime)();
  };
}

/*

Versuch eines Workarounds, damit der ConfirmDialog nach accept oder reject geschlossen wird.
Hat nicht funktioniert.
Überschriebene Funktionen werden aber aufgerufen.

import { ConfirmDialog } from 'primeng/confirmdialog';

ConfirmDialog.prototype.onAccept = function(): void {
  console.warn('ConfirmDialog.prototype.onAccept');
  this.confirmation?.accept?.call(this);
  this.maskVisible = false;
};

ConfirmDialog.prototype.onReject = function(): void {
  console.warn('ConfirmDialog.prototype.onReject');
  this.confirmation?.reject?.call(this);
  this.maskVisible = false;
};

*/

@NgModule({
  declarations: [AppComponent],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    GraphQLModule,
    BrowserModule,
    AppRoutingModule,
    DevExtremeModule,
    SharedModule,
    SharedFrontendModule,
    ToastModule,
    OAuthModule.forRoot(),
    NoopAnimationsModule,
    //BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    ConfirmDialogComponent,
  ],
  providers: [
    {
      provide: OAuthModuleConfig,
      useFactory: oAuthConfigFactory,
    },
    { provide: OAuthStorage, useFactory: storageFactory },
    {
      provide: AUTH_SERVICE_CONFIG_TOKEN,
      useFactory: () => {
        const globalConfig = window.__CONFIG;
        const retVal: AuthServiceConfig = {
          clientId: globalConfig.IDM_CLIENTID,
          issuer: globalConfig.IDM_ISSUER,
          scope: globalConfig.IDM_SCOPE,
          //dummyClientSecret: globalConfig.IDM_CLIENT_SECRET,
          //requireHTTPS: true,
          //responseType: 'code',
        };
        return retVal;
      },
    },
    {
      provide: TRANSLATION_SERVICE_CONFIG_TOKEN,
      useFactory: () => {
        return {
          allowedLocales: allowedLocales,
          defaultLocale: appLocale,
        };
      },
    },
    {
      provide: ACCEPT_LANGUAGE_CONFIG_TOKEN,
      useFactory: () => {
        return { endpoints: [apiUrl] };
      },
    },
    {
      provide: DATETIME_SERVICE_CONFIG_TOKEN,
      useFactory: (): DateTimeConfig => {
        return { defaultDateFormat: defaultDateFormat };
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageInterceptor,
      multi: true,
    },
    [
      {
        provide: APP_INITIALIZER,
        // KS, 20260915: appInitializerWithoutOfflineAccess statt appInitializer --
        // app-lokaler Workaround, Begruendung siehe Kommentar bei der Funktion.
        useFactory: appInitializerWithoutOfflineAccess,
        deps: [AuthService, TranslationService, DateTimeService, OAuthService],
        multi: true,
      },
    ],
    provideHttpClient(withInterceptorsFromDi()),

    // Browser-Titel pro Seite: ersetzt Angulars DefaultTitleStrategy.
    // useExisting (nicht useClass): der Router und die Komponenten, die den
    // Titel setzen, müssen dieselbe Instanz benutzen -- useClass würde neben
    // der providedIn:'root'-Instanz eine zweite anlegen.
    { provide: TitleStrategy, useExisting: PageTitleStrategy },

    // KS TEST 20251030
    ConfirmationService,

    // KS TEST 20260602
    MessageService,

    // KS TEST Router ==> error
    //{ provide: RouteReuseStrategy, useClass: NoReuseStrategy },
  ],
})
export class AppModule {}
